import memoize from "lodash/memoize";
import moment, { Moment } from "moment";
import React from "react";
import { parseTime } from "../utilities";
import { PhosphorClock } from "./Atoms/PhosphorIcon";
import { Input } from "semantic-ui-react";
import { usePrevious, useSemanticUiInputRef } from "utilities/hookutils";
import { PhosphorXButton } from "./Atoms/PhosphorIconButton";

type Step = "QuarterHourly" | "HalfHourly" | "Hourly" | number;
type HourNotation = "12hr" | "24hr";

interface ITimePickerProps {
  /** The interval between the times */
  step?: Step;

  /** The selected value */
  value: Moment;

  /** Fired when a new time has been selected */
  onChange: (time: Moment) => void;

  /** If time should be formatted according to 12 hour or 24 hour standard. By default it will use the locale */
  hourNotation?: HourNotation;

  /** If the time can be left empty. An empty value is a moment with a value of 0 */
  canBeEmpty?: boolean;

  /** If the dropdown should have an error style */
  error?: boolean;

  /** The style for the input field */
  style?: React.CSSProperties;

  autoFocus?: boolean;
}

const TimePicker = (props: ITimePickerProps) => {
  const { step = "QuarterHourly" } = props;

  const [isMenuOpen, setIsMenuOpen] = React.useState(false);

  const formatMoment = (momentToFormat?: Moment): string => {
    const { hourNotation } = props;
    const localeAwareTimeformat = "LT";

    if (momentToFormat == null || momentToFormat.valueOf() === 0) {
      return "";
    }

    switch (hourNotation) {
      case null:
      case undefined: return momentToFormat.format(localeAwareTimeformat);
      case "12hr": return momentToFormat.format("hh:mm A");
      case "24hr": return momentToFormat.format("HH:mm");
      default: throw Error("Unexpected value for hourNotation: " + hourNotation);
    }
  };

  const options = generateMoments(step);

  const normalizedValue = props.value ? normalizeMoment(props.value) : null;
  const previousNormalizedValue = usePrevious(normalizedValue);

  const [inputValue, setInputValue] = React.useState(formatMoment(normalizedValue));
  const [isUsingKeyboardNavigation, setIsUsingKeyboardNavigation] = React.useState(false);
  const [highlightingIndex, setHighlightingIndex] = React.useState(-1);

  const closeMenu = () => {
    setIsMenuOpen(false);
    setIsUsingKeyboardNavigation(false);
  }

  const width = props.canBeEmpty ? 140 : 105;

  const inputRef = useSemanticUiInputRef();
  const menuRef = React.useRef(null);

  // There are two ways to save a value:
  // 1. Clicking on an option
  // 2. Using arrow keys to select an option and pressing enter
  // 3. Pressing enter or tab after typing
  // 4. Clicking outside the menu/text input after typing

  const saveInputValue = React.useCallback((inputValue: string) => {
    if (props.canBeEmpty && inputValue.trim() === "") {
      setInputValue("");
      props.onChange(null);
      return;
    }

    const parsedTimeMoment = parseTextInput(inputValue);
    const newValue = parsedTimeMoment ?? props.value;
    props.onChange(newValue);
    setInputValue(formatMoment(newValue));

  }, [props.onChange, props.value, props.canBeEmpty]);

  const handleClickOutside = React.useCallback((event: MouseEvent) => {
    if (
      menuRef.current &&
      !menuRef.current.contains(event.target) &&
      inputRef.ref.current !== event.target
    ) {
      saveInputValue(inputValue);
      closeMenu();
    }
  }, [menuRef, inputValue, props.onChange, saveInputValue]);

  const onOptionSelected = React.useCallback((moment: Moment) => {
    setInputValue(formatMoment(moment));
    props.onChange(moment);
    closeMenu();
  }, [props.onChange]);

  const onArrowDown = React.useCallback(() => {
    setIsUsingKeyboardNavigation(true);
    setHighlightingIndex(prev => {
      let next: number;
      if ((prev + 1) >= options.length) next = 0;
      else next = prev + 1;

      return next;
    });
  }, [options]);

  const onArrowUp = React.useCallback(() => {
    setIsUsingKeyboardNavigation(true);
    setHighlightingIndex(prev => {
      let next: number;

      if (prev === -1) next = 0;
      else if ((prev - 1) < 0) next = options.length - 1;
      else next = prev - 1;

      return next;
    });
  }, [options]);


  // Up: select the previous option
  // Down: select the next option
  // Enter/Tab after using 'up' and 'down': select the highlighted option
  // Enter/Tab without using 'up' and 'down': save what you've typed in the input field
  const onKeyDown: React.KeyboardEventHandler<HTMLDivElement> = React.useCallback((e) => {
    const isEnterOrTabPressed = e.key === "Enter" || e.key === "Tab";

    if (e.key === "ArrowDown") {
      e.stopPropagation();
      onArrowDown();
    } else if (e.key === "ArrowUp") {
      e.stopPropagation();
      onArrowUp();
    } else if (isUsingKeyboardNavigation && isEnterOrTabPressed) {
      const selectedMoment = options[highlightingIndex];
      if (selectedMoment) {
        onOptionSelected(selectedMoment);
        closeMenu();
        e.stopPropagation();
        e.preventDefault(); // Prevent form from submitting
      }
    }
    else if (!isUsingKeyboardNavigation && isEnterOrTabPressed) {
      saveInputValue(inputValue);
      closeMenu();

      // Propagation is fine for tabs (e.g. go to the next form element)
      if (e.key === "Enter") {
        e.stopPropagation();
        e.preventDefault(); // Prevent form from submitting
      }
    }
    else {
      setIsUsingKeyboardNavigation(false);
    }
  }, [highlightingIndex, inputValue, isUsingKeyboardNavigation, onArrowDown, onArrowUp, onOptionSelected, saveInputValue]);


  // We need a global event listener to detect when a user clicks outside the context menu
  React.useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [inputValue, props.onChange, handleClickOutside, onKeyDown]);

  React.useEffect(() => {
    if (props.autoFocus) {
      inputRef.ref.current.focus();
    }
  }, []);

  // It can happen that the value is changed from the outside
  // Then we need to update the formatted time shown in the input box
  React.useEffect(() => {
    if (previousNormalizedValue?.valueOf() === normalizedValue?.valueOf()) return;

    const formattedValue = formatMoment(normalizedValue);
    if (formattedValue !== inputValue) {
      setInputValue(formattedValue);
    }
  }, [normalizedValue, props.hourNotation, inputValue, previousNormalizedValue])


  const onHoverOption = (index: number) => {
    setHighlightingIndex(index);
  }

  const onInputClick = () => {
    setIsMenuOpen(true);
    setHighlightingIndex(getMatchingMomentIndex(inputValue, step));
  }

  const onFocus = () => {
    inputRef.ref.current.select();
    setIsMenuOpen(true);
    setHighlightingIndex(getMatchingMomentIndex(inputValue, step));
  }

  const onInputChange = (newInput: string) => {
    setInputValue(newInput);
    setHighlightingIndex(getMatchingMomentIndex(newInput, step));
  }

  // Normally "up" (inside the input) will move the cursor to the start of the input field
  // and "down" will move the cursor to the end of the input field
  // We want to prevent this behavior because we use up/down for navigating the menu
  const onInputKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
    }
  }

  return <div className="flex" onKeyDown={onKeyDown}>
    <div style={{ width }}>
      <Input
        ref={inputRef.refSetter}
        value={inputValue}
        onChange={(_, { value }) => onInputChange(value)}
        onFocus={onFocus}
        onClick={onInputClick}
        error={props.error}
        className="icon"
        onKeyDown={onInputKeyDown}
      >
        <input />

        <i className="icon" style={{ top: 6 }}>
          <PhosphorClock iconWeight="fill" />
        </i>
        {props.canBeEmpty && <PhosphorXButton
          style={{
            position: "absolute",
            right: 34,
            top: 6,
          }}
          layout="none"
          onClick={() => saveInputValue("")}
        />}
      </Input>
      {isMenuOpen && <TimeSuggestions
        searchInputRef={inputRef.ref}
        innerRef={menuRef}
        isOpen={isMenuOpen}
        value={inputValue}
        width={width}
        options={options}
        onMomentSelected={onOptionSelected}
        step={step}
        highlightingIndex={highlightingIndex}
        onHoverOption={onHoverOption}
      />}
    </div>
  </div>;
}

export default TimePicker;

interface TimeSuggestionsProps {
  isOpen: boolean;
  value: string;
  width: number
  options: Moment[];
  onMomentSelected: (moment: Moment) => void;
  innerRef: React.RefObject<HTMLDivElement>;
  searchInputRef: React.RefObject<HTMLInputElement>;
  step: Step;

  highlightingIndex: number;
  onHoverOption: (index: number) => void;
}
const TimeSuggestions = (props: TimeSuggestionsProps) => {
  type Display = "above" | "below";

  const [display, setDisplay] = React.useState<Display>(undefined);

  const minHeight = 200;
  const maxHeight = 250;

  React.useLayoutEffect(() => {
    const inputRect = props.searchInputRef.current.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const spaceBelow = windowHeight - inputRect.bottom;
    const spaceAbove = inputRect.top;

    if (spaceBelow < minHeight && spaceAbove > spaceBelow) {
      setDisplay("above");
    } else {
      setDisplay("below");
    }
  });

  React.useEffect(() => {
    if (display == null) return;
    if (props.innerRef.current == null) return;

    if (props.highlightingIndex > -1) {
      scrollOptionIntoView(props.highlightingIndex, props.innerRef.current.children[0] as HTMLDivElement);
    }
  }, [props.highlightingIndex, display]);

  if (display == undefined) {
    return null;
  }



  return <div
    ref={props.innerRef}
    style={{
      position: "absolute",
      minHeight,
      maxHeight,
      border: "1px solid var(--border-active)",
      background: "var(--input-bg)",
      width: props.width,
      transform: display === "below" ? "translate(0, 8px)" : "translate(0, -290px)",
      borderRadius: "0.30769231rem",
      zIndex: 1,
    }}>
    <div
      style={{
        minHeight,
        maxHeight,
        overflowY: "scroll",
      }}
    >
      {props.options.map((option, idx) => (
        <div
          key={option.valueOf()}
          style={{
            padding: "8px 12px",
            cursor: "pointer",
            backgroundColor: props.highlightingIndex === idx ? "var(--primary-color-transparent)" : "transparent"
          }}
          onClick={() => props.onMomentSelected(option)}
          onMouseEnter={() => props.onHoverOption(idx)}
          onMouseLeave={() => props.onHoverOption(-1)}
        >
          <div>{option.format("LT")}</div>
        </div>
      ))}
    </div>
  </div>
}

const scrollOptionIntoView = (index: number, menu: HTMLDivElement) => {
  const targetElement = menu.children[index] as HTMLDivElement;
  const menuRect = menu.getBoundingClientRect();
  const targetRect = targetElement.getBoundingClientRect();

  // Is in view
  if (targetRect.top >= menuRect.top && targetRect.bottom <= menuRect.bottom) {
    return;
  }
  // Is above, so scroll up
  else if (targetRect.top < menuRect.top) {
    menu.scrollTop = targetElement.offsetTop;
  }
  // Is below, so scroll down 
  else {
    menu.scrollTop = targetElement.offsetTop - menuRect.height + targetRect.height;
  }
}

const epoch = "2000-01-01T00:00:00.000Z";
const minutesInADay = 1440;

const generateMoments = memoize((step: Step): Moment[] => {
  const getIntervalInMinutes = (step: Step): number => {
    if (typeof (step) === "number") {
      return step;
    }

    switch (step) {
      case "QuarterHourly": return 15;
      case "HalfHourly": return 30;
      case "Hourly": return 60;
    }
  };

  const startOfDay: Moment = moment.utc(epoch).startOf("day");
  const interval: number = getIntervalInMinutes(step);

  const totalSteps = Math.ceil(minutesInADay / interval);
  const moments: Moment[] = [];

  for (let i = 0; i < totalSteps; i++) {
    const newMoment = startOfDay.clone().add(i * interval, "minutes");
    moments.push(newMoment);
  }

  return moments;
});

// Extract the hour/minute and add it to an epoch moment
// This way we can easily compare moments
const normalizeMoment = (inputMoment: Moment): Moment => {
  const momentFromEpoch = moment.utc(epoch);
  momentFromEpoch.add(inputMoment.hours(), "hours");
  momentFromEpoch.add(inputMoment.minutes(), "minutes");

  return momentFromEpoch;
};

const parseTextInput = memoize((value: string) => {
  const parsedMoment = parseTime(value);
  if (parsedMoment.isValid()) {
    return normalizeMoment(parsedMoment);
  }

  return null;
});


const getMatchingMomentIndex = memoize((searchQuery: string, step: Step) => {
  const parsedTimeMoment = parseTextInput(searchQuery);
  if (!parsedTimeMoment) return -1;

  const moments = generateMoments(step);
  const foundIndex = moments.findIndex(m => m.valueOf() === parsedTimeMoment.valueOf());
  if (foundIndex > -1)
    return foundIndex;

  // If the moment is not found, we need to find the closest moment
  // We choose the moment that's immediately before the parsed time
  for (let i = 0; i < moments.length; i++) {
    if (i + 1 === moments.length) {
      return parsedTimeMoment.isAfter(moments[i]) ? i : -1;
    }

    const currentMoment = moments[i];
    const nextMoment = moments[i + 1];
    if (parsedTimeMoment.isBetween(currentMoment, nextMoment)) {
      return i;
    }
  }

  return -1;
});